import { CPagination, CPaginationItem } from '@coreui/react-pro'
import React, { useMemo } from 'react'
import { reduxServices } from '../../reducers/reduxServices'
import { useAppDispatch, useTypedSelector } from '../../stateStore'
import { CachedPage } from '../../reducers/appSlice'

type PaginationChangeCallbackType = (
  pageNumber: number,
  pageSize: number,
) => void

const OPaginationRevised = ({
  paginationChangeCallback,
}: {
  paginationChangeCallback?: PaginationChangeCallbackType
}): JSX.Element => {
  const dispatch = useAppDispatch()
  const currentPage = useTypedSelector(
    reduxServices.app.selectors.selectRecentPageWithList,
  )
  const pagination = useTypedSelector(
    reduxServices.app.selectors.selectCurrentPagination,
  )

  const dispatchPageConfig = (config: CachedPage) => {
    dispatch(reduxServices.app.actions.setRecentPageWithList(config))
    dispatch(
      reduxServices.app.actions.setStoredCache({
        page: config.pageNumber,
        size: config.pageSize,
      }),
    )
  }

  const handleNextPage = () => {
    const config: CachedPage = {
      locationPath: window.location.pathname,
      pageNumber: currentPage.pageNumber + 1,
      pageSize: currentPage.pageSize,
      range: pagination,
    }
    dispatchPageConfig(config)
    if (paginationChangeCallback)
      paginationChangeCallback(currentPage.pageNumber + 1, currentPage.pageSize)
    window?.scrollTo(0, 0)
  }

  const handlePreviousPage = () => {
    const config: CachedPage = {
      locationPath: window.location.pathname,
      pageNumber: currentPage.pageNumber - 1,
      pageSize: currentPage.pageSize,
      range: pagination,
    }
    dispatchPageConfig(config)
    if (paginationChangeCallback)
      paginationChangeCallback(currentPage.pageNumber - 1, currentPage.pageSize)
    window?.scrollTo(0, 0)
  }

  const handleFirstPage = () => {
    const config: CachedPage = {
      locationPath: window.location.pathname,
      pageNumber: 1,
      pageSize: currentPage.pageSize,
      range: pagination,
    }
    dispatchPageConfig(config)
    if (paginationChangeCallback)
      paginationChangeCallback(1, currentPage.pageSize)
    window?.scrollTo(0, 0)
  }

  const handleLastPage = () => {
    const pageRange = pagination
    const config: CachedPage = {
      locationPath: window.location.pathname,
      pageNumber: pageRange[pageRange.length - 1],
      pageSize: currentPage.pageSize,
      range: pagination,
    }
    dispatchPageConfig(config)
    if (paginationChangeCallback)
      paginationChangeCallback(
        pageRange[pageRange.length - 1],
        currentPage.pageSize,
      )
    window?.scrollTo(0, 0)
  }

  const handleSelectPage = (pageNumber: number) => {
    const config: CachedPage = {
      locationPath: window.location.pathname,
      pageNumber,
      pageSize: currentPage.pageSize,
      range: pagination,
    }
    dispatchPageConfig(config)
    if (paginationChangeCallback)
      paginationChangeCallback(pageNumber, currentPage.pageSize)
    window?.scrollTo(0, 0)
  }

  const paginationItems = useMemo(() => {
    if (pagination.length < 6) {
      return pagination
    }

    if (pagination.includes(currentPage.pageNumber + 4)) {
      return pagination.slice(
        currentPage.pageNumber - 1,
        currentPage.pageNumber + 4,
      )
    } else {
      return pagination.slice(pagination.length - 5, pagination.length)
    }
  }, [pagination])

  return (
    <CPagination>
      <CPaginationItem
        disabled={currentPage.pageNumber === 1}
        onClick={handleFirstPage}
        data-testid="first-page"
      >
        &laquo; First
      </CPaginationItem>
      <CPaginationItem
        disabled={currentPage.pageNumber === 1}
        onClick={handlePreviousPage}
        data-testid="prev-page"
      >
        &#8249; Prev
      </CPaginationItem>
      {paginationItems.map((pageNumber) => {
        return (
          <CPaginationItem
            key={pageNumber}
            data-testid="test-page-number"
            active={currentPage.pageNumber === pageNumber}
            onClick={() => handleSelectPage(pageNumber)}
          >
            {pageNumber}
          </CPaginationItem>
        )
      })}
      <CPaginationItem
        onClick={handleNextPage}
        disabled={currentPage.pageNumber === pagination[pagination.length - 1]}
        data-testid="next-page"
      >
        Next &#8250;
      </CPaginationItem>
      <CPaginationItem
        disabled={currentPage.pageNumber === pagination[pagination.length - 1]}
        onClick={handleLastPage}
        data-testid="last-page"
      >
        Last &raquo;
      </CPaginationItem>
    </CPagination>
  )
}

export default OPaginationRevised
